(() => {
  'use strict';

  const STORAGE_KEY = 'fs_txhs_school_id';
  const ACTIVE_KEY = 'fanscore_active_school_id';
  const PLATFORM_KEY = 'fanscore_active_platform';
  const PLATFORM = 'txhs';

  const esc = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const asColor = (value, fallback) => /^#[0-9a-f]{3,8}$/i.test(value || '') ? value : fallback;

  class TexasFanScoreHome {
    constructor(root) {
      this.root = root;
      this.apiUrl = (root.dataset.supabaseUrl || '').replace(/\/$/, '');
      this.publishableKey = root.dataset.supabasePublishableKey || '';
      this.seasonKey = root.dataset.seasonKey || 'txhs-2026';
      this.defaultSchoolSlug = root.dataset.defaultSchoolSlug || '';
      this.teamRequestUrl = root.dataset.teamRequestUrl || '/pages/fanscore-texas-team-request';
      this.schools = [];
      this.schoolMap = new Map();
      this.currentSchool = null;
      this.currentRankScope = 'state';
      this.abort = new AbortController();
    }

    qs(selector) { return this.root.querySelector(selector); }
    qsa(selector) { return [...this.root.querySelectorAll(selector)]; }

    async init() {
      this.bindUI();
      if (!this.apiUrl || !this.publishableKey) {
        this.showSetupState();
        return;
      }

      try {
        await this.loadSchools();
        await this.loadSavedPreference();
        if (!this.schools.length) {
          this.showNoSchoolsState();
          return;
        }
        const selected = this.resolveInitialSchool();
        await this.selectSchool(selected, { persist: false, closePicker: false });
      } catch (error) {
        console.error('[FanScore TXHS]', error);
        this.showErrorState(error);
      }
    }

    bindUI() {
      const signal = this.abort.signal;
      this.qsa('[data-txfs-open-picker]').forEach((button) => {
        button.addEventListener('click', () => this.openPicker(), { signal });
      });
      this.qs('[data-txfs-close-picker]')?.addEventListener('click', () => this.closePicker(), { signal });
      this.qs('[data-txfs-modal]')?.addEventListener('click', (event) => {
        if (event.target.matches('[data-txfs-modal]')) this.closePicker();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closePicker();
      }, { signal });

      ['[data-txfs-search]', '[data-txfs-association]', '[data-txfs-classification]'].forEach((selector) => {
        const element = this.qs(selector);
        if (!element) return;
        element.addEventListener(element.tagName === 'INPUT' ? 'input' : 'change', () => this.renderPickerList(), { signal });
      });

      this.qsa('[data-txfs-rank-scope]').forEach((button) => {
        button.addEventListener('click', () => {
          this.currentRankScope = button.dataset.txfsRankScope;
          this.qsa('[data-txfs-rank-scope]').forEach((x) => x.classList.toggle('is-active', x === button));
          this.loadRankings();
        }, { signal });
      });
    }

    headers() {
      const accessToken = this.detectAccessToken();
      const headers = { apikey: this.publishableKey, Accept: 'application/json' };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      return headers;
    }

    detectAccessToken() {
      const direct = window.__SUPABASE_ACCESS_TOKEN__ || window.FanScore?.accessToken || null;
      if (direct) return direct;
      try {
        const ref = new URL(this.apiUrl).hostname.split('.')[0];
        const raw = localStorage.getItem(`sb-${ref}-auth-token`);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.access_token || parsed?.currentSession?.access_token || null;
      } catch (_) { return null; }
    }

    async rest(path, params = {}) {
      const url = new URL(`${this.apiUrl}/rest/v1/${path}`);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
      });
      const response = await fetch(url, { headers: this.headers(), signal: this.abort.signal });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`FanScore API ${response.status}: ${body || response.statusText}`);
      }
      return response.json();
    }

    async restWrite(path, { method = 'POST', params = {}, body, prefer = 'return=minimal' } = {}) {
      const url = new URL(`${this.apiUrl}/rest/v1/${path}`);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
      });
      const headers = { ...this.headers(), 'Content-Type': 'application/json', Prefer: prefer };
      const response = await fetch(url, { method, headers, body: JSON.stringify(body), signal: this.abort.signal });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`FanScore write ${response.status}: ${text || response.statusText}`);
      }
      return response.text();
    }

    async loadSavedPreference() {
      const userId = this.detectUserId();
      const token = this.detectAccessToken();
      if (!userId || !token) return;
      try {
        const rows = await this.rest('txhs_fan_preferences', { select: 'school_id', user_id: `eq.${userId}`, limit: '1' });
        if (rows?.[0]?.school_id && this.schoolMap.has(rows[0].school_id)) localStorage.setItem(STORAGE_KEY, rows[0].school_id);
      } catch (error) { console.warn('[FanScore TXHS] preference read skipped', error); }
    }

    async savePreference() {
      const userId = this.detectUserId();
      const token = this.detectAccessToken();
      if (!userId || !token || !this.currentSchool) return;
      try {
        await this.restWrite('txhs_fan_preferences', {
          params: { on_conflict: 'user_id' },
          body: { user_id: userId, school_id: this.currentSchool.school_id, season_key: this.seasonKey, updated_at: new Date().toISOString() },
          prefer: 'resolution=merge-duplicates,return=minimal'
        });
      } catch (error) { console.warn('[FanScore TXHS] preference write skipped', error); }
    }

    async loadSchools() {
      const rows = await this.rest('txhs_school_directory_v', {
        select: 'school_id,slug,name,short_name,logo_url,primary_color,secondary_color,accent_color,association_code,classification,division,region_no,district_code,football_format,city,county,mascot,hero_image_url,stadium_name,stadium_city,stadium_state',
        order: 'name.asc',
        limit: '5000'
      });
      this.schools = rows || [];
      this.schoolMap = new Map(this.schools.map((school) => [school.school_id, school]));
      this.populateFilters();
      this.renderPickerList();
    }

    resolveInitialSchool() {
      const params = new URLSearchParams(location.search);
      const fromQueryId = params.get('fs_school_id');
      const fromQuerySlug = params.get('fs_school_slug');
      const fromStorage = localStorage.getItem(STORAGE_KEY);

      return this.schoolMap.get(fromQueryId)
        || this.schools.find((s) => s.slug === fromQuerySlug)
        || this.schoolMap.get(fromStorage)
        || this.schools.find((s) => s.slug === this.defaultSchoolSlug)
        || this.schools.find((s) => s.association_code === 'UIL')
        || this.schools[0];
    }

    populateFilters() {
      const association = this.qs('[data-txfs-association]');
      const classification = this.qs('[data-txfs-classification]');
      if (association) {
        const values = [...new Set(this.schools.map((x) => x.association_code).filter(Boolean))].sort();
        association.innerHTML = '<option value="">All associations</option>' + values.map((x) => `<option value="${esc(x)}">${esc(x)}</option>`).join('');
      }
      if (classification) {
        const values = [...new Set(this.schools.map((x) => x.classification).filter(Boolean))]
          .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
        classification.innerHTML = '<option value="">All levels</option>' + values.map((x) => `<option value="${esc(x)}">${esc(x)}</option>`).join('');
      }
    }

    renderPickerList() {
      const list = this.qs('[data-txfs-school-list]');
      if (!list) return;
      const search = (this.qs('[data-txfs-search]')?.value || '').trim().toLowerCase();
      const association = this.qs('[data-txfs-association]')?.value || '';
      const classification = this.qs('[data-txfs-classification]')?.value || '';
      const matches = this.schools.filter((school) => {
        const haystack = [school.name, school.short_name, school.mascot, school.city, school.district_code].filter(Boolean).join(' ').toLowerCase();
        return (!search || haystack.includes(search))
          && (!association || school.association_code === association)
          && (!classification || school.classification === classification);
      }).slice(0, 250);

      if (!matches.length) {
        list.innerHTML = `<div class="txfs-empty">No football programs match those filters.<br><a href="${esc(this.teamRequestUrl)}" style="text-decoration:underline">Request or claim this team</a></div>`;
        return;
      }

      list.innerHTML = matches.map((school) => {
        const logo = school.logo_url
          ? `<img src="${esc(school.logo_url)}" alt="" loading="lazy">`
          : '<div class="txfs-option-mark">TX</div>';
        return `<button type="button" class="txfs-school-option" data-school-id="${esc(school.school_id)}">
          ${logo}
          <span><strong>${esc(school.name)}</strong><small>${esc(this.schoolMeta(school))}</small></span>
        </button>`;
      }).join('');

      list.querySelectorAll('[data-school-id]').forEach((button) => {
        button.addEventListener('click', () => {
          const school = this.schoolMap.get(button.dataset.schoolId);
          if (school) this.selectSchool(school, { persist: true, closePicker: true });
        }, { signal: this.abort.signal });
      });
    }

    schoolMeta(school) {
      const level = [school.classification, school.division].filter(Boolean).join(' ');
      const district = school.district_code ? `District ${school.district_code}` : '';
      return [school.association_code, level, district, school.football_format].filter(Boolean).join(' | ');
    }

    async selectSchool(school, options = {}) {
      if (!school) return;
      this.currentSchool = school;
      if (options.persist !== false) {
        localStorage.setItem(STORAGE_KEY, school.school_id);
        localStorage.setItem(ACTIVE_KEY, school.school_id);
        localStorage.setItem(PLATFORM_KEY, PLATFORM);
      }
      window.__FS_ACTIVE_SCHOOL_ID__ = school.school_id;
      window.__FS_ACTIVE_SCHOOL_SLUG__ = school.slug;
      window.__FS_ACTIVE_PLATFORM__ = PLATFORM;
      window.dispatchEvent(new CustomEvent('fanscore:school-context-changed', {
        detail: { platform: PLATFORM, school_id: school.school_id, school_slug: school.slug }
      }));

      this.renderSchool();
      this.updateGameLinks();
      if (options.persist !== false) this.savePreference();
      if (options.closePicker) this.closePicker();

      await Promise.allSettled([
        this.loadNextGame(),
        this.loadSponsor(),
        this.loadRankings(),
        this.loadFanScore()
      ]);
    }

    renderSchool() {
      const s = this.currentSchool;
      const primary = asColor(s.primary_color, '#8f1d14');
      const secondary = asColor(s.secondary_color, '#d8c7a4');
      const accent = asColor(s.accent_color, secondary);
      this.root.style.setProperty('--team', primary);
      this.root.style.setProperty('--team2', secondary);
      this.root.style.setProperty('--team-accent', accent);
      this.root.style.setProperty('--teamText', this.contrast(primary));
      if (s.hero_image_url) this.root.style.setProperty('--hero-image', `linear-gradient(115deg,rgba(0,0,0,.24),rgba(0,0,0,.82)),url("${String(s.hero_image_url).replaceAll('"', '%22')}")`);
      else this.root.style.removeProperty('--hero-image');

      this.setText('[data-txfs-school-name]', s.short_name || s.name);
      this.setText('[data-txfs-team-button-name]', s.short_name || s.name);
      this.setText('[data-txfs-school-meta]', this.schoolMeta(s));
      const logo = this.qs('[data-txfs-school-logo]');
      if (logo) {
        logo.src = s.logo_url || '';
        logo.alt = s.name ? `${s.name} logo` : '';
        logo.classList.toggle('is-empty', !s.logo_url);
      }
    }

    contrast(hex) {
      const value = String(hex).replace('#', '');
      if (![3, 6].includes(value.length)) return '#fff';
      const full = value.length === 3 ? value.split('').map((x) => x + x).join('') : value;
      const r = parseInt(full.slice(0, 2), 16), g = parseInt(full.slice(2, 4), 16), b = parseInt(full.slice(4, 6), 16);
      return ((r * 299 + g * 587 + b * 114) / 1000) > 150 ? '#111' : '#fff';
    }

    async loadNextGame() {
      const s = this.currentSchool;
      const now = new Date().toISOString();
      const games = await this.rest('txhs_next_games_v', {
        select: '*',
        or: `(home_school_id.eq.${s.school_id},away_school_id.eq.${s.school_id})`,
        kickoff_at: `gte.${now}`,
        order: 'kickoff_at.asc',
        limit: '1'
      });
      this.renderNextGame(games?.[0]);
    }

    renderNextGame(game) {
      const target = this.qs('[data-txfs-matchup]');
      if (!target) return;
      if (!game) {
        target.innerHTML = '<div class="txfs-empty">Schedule is ready for import. No upcoming game is loaded for this program yet.</div>';
        this.setText('[data-txfs-next-label]', 'Next Friday');
        return;
      }
      const kickoff = game.kickoff_at ? new Date(game.kickoff_at) : null;
      const date = kickoff ? kickoff.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : 'TBA';
      const time = kickoff ? kickoff.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'TBA';
      const homeLogo = game.home_logo_url ? `<img src="${esc(game.home_logo_url)}" alt="">` : '';
      const awayLogo = game.away_logo_url ? `<img src="${esc(game.away_logo_url)}" alt="">` : '';
      target.innerHTML = `<div class="txfs-matchup-team">${awayLogo}<strong>${esc(game.away_name)}</strong></div>
        <div class="txfs-at">AT</div>
        <div class="txfs-matchup-team">${homeLogo}<strong>${esc(game.home_name)}</strong></div>
        <div class="txfs-game-detail" style="grid-column:1/-1">
          <span class="txfs-pill">${esc(date)}</span><span class="txfs-pill">${esc(time)}</span>
          ${game.venue_name ? `<span class="txfs-pill">${esc(game.venue_name)}</span>` : ''}
          ${game.district_game ? '<span class="txfs-pill">District Game</span>' : ''}
          ${game.playoff_round ? `<span class="txfs-pill">${esc(game.playoff_round)}</span>` : ''}
        </div>`;
      this.setText('[data-txfs-next-label]', date);
    }

    async loadSponsor() {
      const s = this.currentSchool;
      const now = new Date().toISOString();
      const rows = await this.rest('txhs_sponsor_slots', {
        select: 'id,school_id,placement_key,sponsor_name,logo_url,href,starts_at,ends_at',
        placement_key: 'eq.homepage_header',
        or: `(school_id.eq.${s.school_id},school_id.is.null)`,
        active: 'eq.true',
        order: 'school_id.desc.nullslast,created_at.desc',
        limit: '10'
      });
      const sponsor = (rows || []).find((row) => (!row.starts_at || row.starts_at <= now) && (!row.ends_at || row.ends_at >= now));
      this.renderSponsor(sponsor);
    }

    renderSponsor(sponsor) {
      const box = this.qs('[data-txfs-sponsor]');
      if (!box || !sponsor) return;
      const logo = sponsor.logo_url ? `<img src="${esc(sponsor.logo_url)}" alt="${esc(sponsor.sponsor_name)}">` : '';
      const inner = `${logo}<span><small>Friday Night Partner</small><strong>${esc(sponsor.sponsor_name)}</strong></span>`;
      box.innerHTML = sponsor.href ? `<a href="${esc(sponsor.href)}" target="_blank" rel="noopener">${inner}</a>` : inner;
    }

    scopeKey(scope) {
      const s = this.currentSchool;
      if (scope === 'state') return 'TX';
      if (scope === 'association') return s.association_code;
      if (scope === 'classification') return [s.association_code, s.classification, s.division].filter(Boolean).join(':');
      return [s.association_code, s.classification, s.division, s.district_code].filter(Boolean).join(':');
    }

    async loadRankings() {
      const scope = this.currentRankScope || 'state';
      const scopeKey = this.scopeKey(scope);
      if (!scopeKey) return this.renderRankings([]);
      const rows = await this.rest('txhs_rankings_cache', {
        select: 'school_id,score,fan_count,rank_no',
        season_key: `eq.${this.seasonKey}`,
        scope: `eq.${scope}`,
        scope_key: `eq.${scopeKey}`,
        order: 'rank_no.asc',
        limit: '10'
      });
      this.renderRankings(rows || []);
    }

    renderRankings(rows) {
      const list = this.qs('[data-txfs-rank-list]');
      if (!list) return;
      if (!rows.length) {
        list.innerHTML = '<div class="txfs-empty">Rankings populate as fans play and the leaderboard cache refreshes.</div>';
        return;
      }
      list.innerHTML = rows.map((row) => {
        const school = this.schoolMap.get(row.school_id) || { name: 'Texas Football Program', logo_url: '' };
        const logo = school.logo_url ? `<img src="${esc(school.logo_url)}" alt="">` : '';
        return `<div class="txfs-rank-row">
          <div class="txfs-rank-pos">#${esc(row.rank_no || '-')}</div>
          <div class="txfs-rank-school">${logo}<span>${esc(school.short_name || school.name)}</span></div>
          <div class="txfs-rank-score">${Number(row.score || 0).toLocaleString()}<small>${Number(row.fan_count || 0).toLocaleString()} fans</small></div>
        </div>`;
      }).join('');
    }

    async loadFanScore() {
      const userId = this.detectUserId();
      if (!userId) {
        const globalScore = this.detectGlobalScore();
        if (globalScore !== null) this.setText('[data-txfs-fanscore]', Number(globalScore).toLocaleString());
        return;
      }
      const rows = await this.rest('txhs_user_leaderboard_cache', {
        select: 'score,rank_no,plays_count',
        season_key: `eq.${this.seasonKey}`,
        school_id: `eq.${this.currentSchool.school_id}`,
        user_id: `eq.${userId}`,
        limit: '1'
      });
      const row = rows?.[0];
      if (!row) return;
      this.setText('[data-txfs-fanscore]', Number(row.score || 0).toLocaleString());
      this.setText('[data-txfs-school-rank]', row.rank_no ? `#${row.rank_no}` : '-');
      this.setText('[data-txfs-plays]', Number(row.plays_count || 0).toLocaleString());
    }

    detectUserId() {
      const direct = window.__FS_USER_ID__
        || window.FanScore?.user?.id
        || window.fanscoreUser?.id
        || document.documentElement.dataset.fanscoreUserId
        || null;
      if (direct) return direct;
      const token = this.detectAccessToken();
      if (!token || token.split('.').length < 2) return null;
      try {
        const payload = token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/');
        return JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')))?.sub || null;
      } catch (_) { return null; }
    }

    detectGlobalScore() {
      const candidates = [window.FanScore?.lifetimeScore, window.FanScore?.score, window.__FS_LIFETIME_SCORE__];
      const value = candidates.find((x) => x !== undefined && x !== null && x !== '');
      return Number.isFinite(Number(value)) ? Number(value) : null;
    }

    updateGameLinks() {
      const s = this.currentSchool;
      this.qsa('[data-txfs-game-link]').forEach((link) => {
        const base = link.dataset.baseHref || link.getAttribute('href') || '#';
        link.dataset.baseHref = base;
        try {
          const url = new URL(base, location.origin);
          url.searchParams.set('platform', PLATFORM);
          url.searchParams.set('fs_school_id', s.school_id);
          url.searchParams.set('fs_school_slug', s.slug);
          link.href = url.pathname + url.search + url.hash;
        } catch (_) {
          link.href = base;
        }
      });
      this.qsa('[data-txfs-context-link]').forEach((link) => {
        const base = link.dataset.baseHref || link.getAttribute('href') || '#';
        link.dataset.baseHref = base;
        try {
          const url = new URL(base, location.origin);
          url.searchParams.set('platform', PLATFORM);
          url.searchParams.set('fs_school_id', s.school_id);
          link.href = url.pathname + url.search + url.hash;
        } catch (_) {}
      });
    }

    openPicker() {
      const modal = this.qs('[data-txfs-modal]');
      if (!modal) return;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      setTimeout(() => this.qs('[data-txfs-search]')?.focus(), 30);
    }

    closePicker() {
      const modal = this.qs('[data-txfs-modal]');
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    }

    setText(selector, value) {
      const node = this.qs(selector);
      if (node) node.textContent = value ?? '';
    }

    showSetupState() {
      this.setText('[data-txfs-school-name]', 'Texas High School Football');
      this.setText('[data-txfs-school-meta]', 'Connect the Hub Supabase URL and publishable key in this section');
      const list = this.qs('[data-txfs-rank-list]');
      if (list) list.innerHTML = '<div class="txfs-empty">The homepage shell is installed. Add the Supabase URL and publishable key to load every football program.</div>';
    }

    showNoSchoolsState() {
      this.setText('[data-txfs-school-name]', 'Texas High School Football');
      this.setText('[data-txfs-school-meta]', 'Run the team importer to activate school selection');
    }

    showErrorState(error) {
      this.setText('[data-txfs-school-name]', 'Texas High School Football');
      this.setText('[data-txfs-school-meta]', 'Data connection needs attention');
      const list = this.qs('[data-txfs-rank-list]');
      if (list) list.innerHTML = `<div class="txfs-empty">${esc(error?.message || 'Unable to load FanScore data.')}</div>`;
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-txfs-root]:not([data-txfs-booted])').forEach((root) => {
      root.dataset.txfsBooted = 'true';
      new TexasFanScoreHome(root).init();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
